/**
 * Matching the file's client column to real clients, and creating the ones that don't
 * exist yet.
 *
 * Lives here rather than in the screen because it makes two decisions that must not be
 * gotten wrong, and both are only testable with the I/O injected:
 *
 * 1. A phone hit must be turned into the FULL client record before it goes on a sale. In
 *    Supabase read mode `findClientByPhone` answers from the lightweight client search,
 *    which omits fields the sale flow needs (see the `getClientById` docblock in
 *    global/Firebase/clients.ts). A sale stores its client as a snapshot that historical
 *    records render from, so a thin snapshot is permanent.
 * 2. Creating a client must be safe to retry. Duplicates split a customer's purchase total
 *    and bonus balance across two records, and that cannot be un-split later.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- client docs are untyped across Firestore and Supabase read modes */

export type ClientResolutionAction = 'existing' | 'create' | 'skip'

export interface ClientResolution {
  action: ClientResolutionAction
  client: any | null
  /** The lookup itself failed — NOT the same as "no such client". */
  failed?: boolean
}

export type ClientResolutions = Record<string, ClientResolution>

export interface ClientLookupDeps {
  /** `findClientByPhone` — may return a lightweight search hit. */
  findByPhone: (phone: string) => Promise<any | null>
  /** `getClientById` — the full record. */
  loadById: (firestoreId: string) => Promise<any | null>
}

/**
 * Firestore read mode returns the doc with `id` set to the Firestore id; Supabase read
 * mode returns both, and `getClientById` wants the Firestore one.
 */
/**
 * Render a normalized phone back in the local format for display and for the name of a
 * client the import creates. Without this a created client is called "77000000001" —
 * permanent, unreadable data in the customer's client list.
 */
export const formatPhoneForDisplay = (phone: string): string => {
  if (/^7\d{10}$/.test(phone)) {
    return `+7 ${phone.slice(1, 4)} ${phone.slice(4, 7)} ${phone.slice(7, 9)} ${phone.slice(9)}`
  }
  return phone
}

export const clientFirestoreId = (client: any): string | null =>
  client?.firestoreId || client?.id || null

/**
 * Turn a phone hit into the full client record.
 *
 * Throws rather than falling back to the thin record: a phone that matched but whose
 * client cannot be loaded is not "no such client", and the caller surfaces it for the
 * user to resolve instead of silently storing an incomplete snapshot.
 */
export const loadFullClient = async (client: any, deps: ClientLookupDeps): Promise<any> => {
  const firestoreId = clientFirestoreId(client)
  if (!firestoreId) return client
  const full = await deps.loadById(firestoreId)
  if (!full) throw new Error(`Client ${firestoreId} matched by phone but could not be loaded`)
  return full
}

/**
 * Look every distinct phone in the file up. A failed lookup is recorded as `skip` +
 * `failed`, never as "not found" — treating a network error as "no such client" would
 * silently create a duplicate of a customer who already exists.
 */
export const resolveClientsByPhone = async (
  phones: string[],
  deps: ClientLookupDeps,
  onError?: (phone: string, error: unknown) => void
): Promise<ClientResolutions> => {
  const resolutions: ClientResolutions = {}

  for (const phone of phones) {
    try {
      const hit = await deps.findByPhone(phone)
      resolutions[phone] = hit
        ? { action: 'existing', client: await loadFullClient(hit, deps) }
        : { action: 'create', client: null }
    } catch (error) {
      onError?.(phone, error)
      resolutions[phone] = { action: 'skip', client: null, failed: true }
    }
  }

  return resolutions
}

export interface ClientCreationDeps extends ClientLookupDeps {
  /** `addNewClient` — returns the created client, already carrying its id. */
  create: (phone: string) => Promise<any>
  /**
   * Called the instant a client is resolved, so the caller can persist progress. Without
   * this, a failure on client N makes clients 1..N-1 look uncreated and the retry creates
   * them a second time.
   */
  onResolved?: (phone: string, resolution: ClientResolution) => void
}

/**
 * Create every client the user chose to create.
 *
 * Re-checks by phone immediately before creating: the client may have appeared since the
 * mapping step — another device, another importer, or an earlier attempt of this same
 * import that failed partway. `clients` has no unique index on the phone number, so this
 * narrows the find-or-create window rather than closing it.
 *
 * Rethrows on failure (the caller aborts the import), but every client resolved before the
 * failure has already been reported through `onResolved`, so a retry skips it.
 */
export const createMissingClients = async (
  resolutions: ClientResolutions,
  deps: ClientCreationDeps
): Promise<ClientResolutions> => {
  const resolved: ClientResolutions = { ...resolutions }

  for (const [phone, resolution] of Object.entries(resolutions)) {
    if (resolution.action !== 'create' || resolution.client) continue

    const existing = await deps.findByPhone(phone)
    resolved[phone] = existing
      ? { action: 'existing', client: await loadFullClient(existing, deps) }
      : { action: 'existing', client: await deps.create(phone) }

    deps.onResolved?.(phone, resolved[phone])
  }

  return resolved
}

/** The client to attach to an order, or null when the order should have none. */
export const clientForOrder = (
  resolutions: ClientResolutions,
  clientKey: string | null
): any | null => {
  if (!clientKey) return null
  const resolution = resolutions[clientKey]
  if (!resolution || resolution.action === 'skip') return null
  return resolution.client || null
}
